/**
 * The freshness note — beside the score, never instead of it.
 *
 * Four renderings, and the two that matter most are the two a single grey badge would
 * have merged:
 *
 *  - **current** — a `low` `Badge`, icon `circle-check`. Nothing has moved.
 *  - **stale, scheduled** — a `medium` `StatusNote` (`role="status"`), whose own icon
 *    is `SEVERITY_ICON.medium` = `circle-alert`, carrying an inner `clock` badge.
 *    A run is coming; wait.
 *  - **stale, requires_request** — a `high` `StatusNote` with `role="alert"`, whose
 *    own icon is `triangle-alert`, carrying an inner **`ban`** badge. **Nobody is
 *    coming**; ask.
 *  - **unknown** — a `medium` `StatusNote` and **no inner badge at all**, so it is not
 *    confusable with `scheduled` despite sharing that severity: it makes no claim
 *    about what will happen, because it does not know.
 *
 * The two stale cases therefore differ in **four** independent channels — severity
 * token, the note's own silhouette, an inner badge whose silhouette is the opposite
 * metaphor (a clock against a prohibition sign), and the words. `docs/UI_PROCESS.md`
 * §4 asks for a greyscale screenshot in which every severity is still
 * distinguishable; two of those four survive it and the words survive everything.
 *
 * `stale-manual` is `high` and `role="alert"` because the wrong action is the
 * *default* one: a person shown "out of date" assumes a system will fix it, and for a
 * period older than the sweep's window nothing ever will. The note therefore says in
 * as many words that **no scheduled run will pick it up**, and names the endpoint
 * that would — rather than leaving "why is it still wrong tomorrow" for them to find
 * out by waiting.
 *
 * ── It must not read as an error, and it must not read as missing data ──────
 *
 * Both are live confusions on this exact panel, which already renders a
 * low-confidence note and a missing-data list. So every stale rendering opens by
 * saying **the number below is real** and that the person may still question it —
 * `confidence` and `data_completeness` describe what the run could see, and a stale
 * score is very often fully confident because the commonest cause is the subject's
 * own correction being approved.
 *
 * ── `current` is rendered, not omitted ──────────────────────────────────────
 *
 * An omitted "everything is fine" is indistinguishable from a build that forgot to
 * check. Rendering it makes the absence of the warning a statement somebody made,
 * which is the same argument `LastSync` makes one screen over.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { readFreshness, type ChangedInput, type FreshnessReading } from '../../scoring/freshness';
import { Badge } from '../../states/Badge';
import { StatusNote } from '../../states/StatusNote';

/** Exhaustive: a fifth input name on the wire is a compile error here. */
const INPUT_LABEL: Readonly<Record<ChangedInput, MessageKey>> = {
  algorithm_version: 'score.freshness.input.algorithm',
  daily_aggregate: 'score.freshness.input.dailyAggregate',
  attendance_session: 'score.freshness.input.attendanceSession',
  time_entry: 'score.freshness.input.timeEntry',
};

const UNKNOWN_REASON: Readonly<Record<'absent' | 'malformed' | 'contradictory', MessageKey>> = {
  absent: 'score.freshness.unknown.absent',
  malformed: 'score.freshness.unknown.malformed',
  contradictory: 'score.freshness.unknown.contradictory',
};

function ChangedInputs({ changed }: { readonly changed: readonly ChangedInput[] }): ReactNode {
  const { t } = useI18n();
  if (changed.length === 0) {
    // `stale` with no *recognised* input name still means something moved — the wire
    // named an input this build has no word for. Said, rather than rendered as an
    // empty list that reads as "nothing in particular".
    return (
      <p data-testid="score-freshness-inputs-unnamed">{t('score.freshness.inputs.unnamed')}</p>
    );
  }
  return (
    <ul className="wp-list" data-testid="score-freshness-inputs">
      {changed.map((input) => (
        <li key={input} data-testid={`score-freshness-input-${input}`}>
          {t(INPUT_LABEL[input])}
        </li>
      ))}
    </ul>
  );
}

export function FreshnessNote({ freshness }: { readonly freshness: unknown }): ReactNode {
  const { t } = useI18n();
  const reading: FreshnessReading = readFreshness(freshness);

  if (reading.kind === 'current') {
    return (
      <p className="wp-flags">
        <Badge
          severity="low"
          icon="circle-check"
          label={t('score.freshness.current')}
          title={t('score.freshness.current.help')}
          testId="score-freshness-current"
        />
      </p>
    );
  }

  if (reading.kind === 'unknown') {
    return (
      <StatusNote
        severity="medium"
        title={t('score.freshness.unknown.title')}
        testId="score-freshness-unknown"
      >
        <p>{t(UNKNOWN_REASON[reading.reason])}</p>
        <p>{t('score.freshness.stillReal')}</p>
      </StatusNote>
    );
  }

  const scheduled = reading.kind === 'stale-scheduled';
  return (
    <StatusNote
      severity={scheduled ? 'medium' : 'high'}
      role={scheduled ? 'status' : 'alert'}
      title={t(scheduled ? 'score.freshness.scheduled.title' : 'score.freshness.manual.title')}
      testId={scheduled ? 'score-freshness-scheduled' : 'score-freshness-manual'}
    >
      {/* First, before the advice: the number below is real. This panel already
          carries a low-confidence note and a missing-data list, and a stale score is
          neither — it is very often fully confident, because the commonest cause of
          staleness is this person's own correction being approved. */}
      <p data-testid="score-freshness-still-real">{t('score.freshness.stillReal')}</p>
      {/* The opposite metaphor, on purpose: a clock (something is coming) against a
          prohibition sign (nothing is). Two silhouettes a reader can tell apart with
          the colour gone, which is what §4's greyscale screenshot actually tests. */}
      <p className="wp-flags">
        <Badge
          severity={scheduled ? 'medium' : 'high'}
          icon={scheduled ? 'clock' : 'ban'}
          label={t(scheduled ? 'score.freshness.scheduled.tag' : 'score.freshness.manual.tag')}
          testId={scheduled ? 'score-freshness-tag-scheduled' : 'score-freshness-tag-manual'}
        />
      </p>
      <p data-testid={scheduled ? 'score-freshness-wait' : 'score-freshness-ask'}>
        {t(scheduled ? 'score.freshness.scheduled.body' : 'score.freshness.manual.body')}
      </p>
      {scheduled ? null : (
        // The sentence that stops somebody waiting for a run that is never coming.
        <p data-testid="score-freshness-nobody-coming">{t('score.freshness.manual.nobody')}</p>
      )}
      <p className="wp-small wp-muted">{t('score.freshness.inputs.heading')}</p>
      <ChangedInputs changed={reading.changed} />
    </StatusNote>
  );
}
